'use client';

import { useState } from 'react';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Skeleton,
} from '@/components/ui';
import {
  type HumanReviewDecisionResult,
  type HumanReviewRequest,
  type HumanReviewRequestFilter,
  type HumanReviewRequestStateFilter,
  type HumanReviewSort,
  isExpiredReadOnlyConflict,
  isHumanReviewDecisionConflict,
} from '@/lib/admin/client';
import { useHumanReviewDecision } from '@/lib/query/useHumanReviewDecision';
import { useHumanReviewRequest } from '@/lib/query/useHumanReviewRequest';
import {
  humanReviewDisabled,
  humanReviewFacets,
  humanReviewRequests,
  useHumanReviewRequests,
} from '@/lib/query/useHumanReviewRequests';

import { HumanReviewDecisionHistory } from './HumanReviewDecisionHistory';
import { HumanReviewRequestCard } from './HumanReviewRequestCard';
import { HumanReviewToolbar } from './HumanReviewToolbar';
import { PanelTitle } from './PanelTitle';
import { humanReviewRequestToTrace } from './traceAdapters';
import { TraceDrilldown } from './TraceDrilldown';

// Pending worklists read priority-lane-then-oldest; decided/history views read
// newest-first (the backend applies the priority lane ordering within 'oldest').
function defaultSortFor(state: HumanReviewRequestStateFilter): HumanReviewSort {
  return state === 'pending' || state === 'changes_requested'
    ? 'oldest'
    : 'newest';
}

const INITIAL_FILTER: HumanReviewRequestFilter = {
  state: 'pending',
  subjectType: null,
  priority: null,
  decisionSource: null,
  sort: 'oldest',
};

export type HumanReviewRequestsPanelProps = {
  filter?: HumanReviewRequestFilter;
  onFilterChange?: (filter: HumanReviewRequestFilter) => void;
};

export function HumanReviewRequestsPanel({
  filter: controlledFilter,
  onFilterChange,
}: HumanReviewRequestsPanelProps = {}) {
  const [uncontrolledFilter, setUncontrolledFilter] =
    useState<HumanReviewRequestFilter>(INITIAL_FILTER);
  const isControlled =
    controlledFilter !== undefined && onFilterChange !== undefined;
  const filter = isControlled ? controlledFilter : uncontrolledFilter;
  const [reasonById, setReasonById] = useState<Record<string, string>>({});
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const queue = useHumanReviewRequests(filter);
  const detail = useHumanReviewRequest(selectedItemId);
  const decide = useHumanReviewDecision();

  const isDisabled = humanReviewDisabled(queue.data);
  const items = humanReviewRequests(queue.data);
  const facets = humanReviewFacets(queue.data);
  const selectedDetail =
    detail.data?.status === 'available' ? detail.data.data : null;
  const selectedItem =
    selectedDetail?.request ?? items.find((item) => item.id === selectedItemId);

  const conflict = isHumanReviewDecisionConflict(decide.error);
  const expiredReadOnly = isExpiredReadOnlyConflict(decide.error);
  const decideFailed = decide.isError && !conflict && !expiredReadOnly;

  const hasItems = items.length > 0;
  const showEmpty =
    !queue.isLoading && !queue.isError && !isDisabled && !hasItems;
  const countLabel =
    filter.state === 'pending'
      ? `${items.length} pending`
      : `${items.length} shown`;

  const updateFilter = (patch: Partial<HumanReviewRequestFilter>) => {
    const nextFilter = { ...filter, ...patch };
    if (isControlled) {
      onFilterChange(nextFilter);
    } else {
      setUncontrolledFilter(nextFilter);
    }
    setSelectedItemId(null);
    decide.reset();
  };

  const setReason = (itemId: string, reason: string) =>
    setReasonById((current) => ({ ...current, [itemId]: reason }));

  const runDecide = (
    request: HumanReviewRequest,
    result: HumanReviewDecisionResult
  ) => decide.mutate({ request, result, reason: reasonById[request.id] ?? '' });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <PanelTitle>Queue</PanelTitle>
          <Badge variant={isDisabled ? 'outline' : 'secondary'}>
            {isDisabled ? 'Disabled' : countLabel}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-4">
        {!isDisabled && (
          <HumanReviewToolbar
            state={filter.state}
            onReviewStateChange={(state) =>
              updateFilter({ state, sort: defaultSortFor(state) })
            }
            subjectType={filter.subjectType}
            onItemTypeChange={(subjectType) => updateFilter({ subjectType })}
            priority={filter.priority}
            onPriorityChange={(priority) => updateFilter({ priority })}
            decisionSource={filter.decisionSource}
            onDecisionSourceChange={(decisionSource) =>
              updateFilter({ decisionSource })
            }
            sort={filter.sort}
            onSortChange={(sort) => updateFilter({ sort })}
            facets={facets}
          />
        )}

        {conflict && (
          <div
            role="alert"
            className="border-border bg-muted flex flex-wrap items-center justify-between gap-2 rounded-md border p-3 text-sm"
          >
            <span>
              This item changed since you loaded it — the queue has been
              refreshed. Re-review and decide again.
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => decide.reset()}
            >
              Dismiss
            </Button>
          </div>
        )}
        {expiredReadOnly && (
          <div
            role="alert"
            className="border-border bg-muted flex flex-wrap items-center justify-between gap-2 rounded-md border p-3 text-sm"
          >
            <span>
              This item expired before your decision was saved. Reopen it before
              deciding again.
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => decide.reset()}
            >
              Dismiss
            </Button>
          </div>
        )}
        {decideFailed && (
          <p className="text-destructive text-sm">
            The decision could not be saved. Please try again.
          </p>
        )}

        {queue.isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        )}
        {queue.isError && (
          <p className="text-destructive text-sm">
            Review queue could not be loaded.
          </p>
        )}
        {isDisabled && (
          <p className="text-muted-foreground text-sm">
            MIND_REVIEW_QUEUE_ENABLED is off for this backend, so the queue
            routes are not mounted.
          </p>
        )}
        {showEmpty && (
          <p className="text-muted-foreground text-sm">
            {filter.state === 'pending'
              ? 'No pending items to review.'
              : 'No review items match this filter.'}
          </p>
        )}

        {items.map((item) => (
          <HumanReviewRequestCard
            key={item.id}
            item={item}
            reason={reasonById[item.id] ?? ''}
            onReasonChange={(reason) => setReason(item.id, reason)}
            onDecide={(decision) => runDecide(item, decision)}
            onShowDetails={() => setSelectedItemId(item.id)}
            isDeciding={decide.isPending}
            isSelected={item.id === selectedItemId}
          />
        ))}

        {queue.hasNextPage && (
          <div className="flex justify-center">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => queue.fetchNextPage()}
              disabled={queue.isFetchingNextPage}
            >
              {queue.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        )}

        {selectedItem && (
          <>
            <div data-testid="review-item-trace">
              <TraceDrilldown
                node={humanReviewRequestToTrace(
                  selectedItem,
                  selectedDetail?.decisions ?? []
                )}
              />
            </div>
            <HumanReviewDecisionHistory
              item={selectedItem}
              decisions={selectedDetail?.decisions ?? []}
              isLoading={detail.isLoading}
              isError={detail.isError}
              reason={reasonById[selectedItem.id] ?? ''}
              onReasonChange={(reason) => setReason(selectedItem.id, reason)}
              onDecide={(decision) => runDecide(selectedItem, decision)}
              isDeciding={decide.isPending}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
